import { useEffect, useMemo, useState } from "react";
import { supabase } from "../lib/supabase";

type Q = {
  id: string;
  event_id: string;
  label: string;
  field_key: string;
  kind: "text" | "email" | "phone" | "bool" | "select" | "multi";
  required: boolean;
  options: any[];
  position: number;
};

const card: React.CSSProperties = { background:"#fff", border:"1px solid #e5e7eb", borderRadius:12, padding:12, marginBottom:12 };
const input: React.CSSProperties = { padding:"10px 12px", borderRadius:10, border:"1px solid #e5e7eb", width:"100%" };
const row: React.CSSProperties = { display:"grid", gridTemplateColumns:"1fr", gap:8 };
const btn: React.CSSProperties = { padding:"10px 12px", borderRadius:10, border:"1px solid #e5e7eb", background:"#fff", fontWeight:700, cursor:"pointer" };
const primaryBtn: React.CSSProperties = { ...btn, background:"#0B2E5B", color:"#fff", border:"none" };
const tinyBtn: React.CSSProperties = { padding:"6px 10px", borderRadius:8, border:"1px solid #e5e7eb", background:"#fff", cursor:"pointer" };

const KINDS = ["text","email","phone","bool","select","multi"] as const;

function slugify(s: string) {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "").slice(0, 40);
}

export default function EventQuestionsEditor({ eventId }: { eventId: string }) {
  const [loading, setLoading] = useState(true);
  const [qs, setQs] = useState<Q[]>([]);
  const [msg, setMsg] = useState<string|null>(null);
  const [err, setErr] = useState<string|null>(null);

  // New question form
  const [label, setLabel] = useState("");
  const [kind, setKind] = useState<Q["kind"]>("text");
  const [required, setRequired] = useState(false);
  const [optionsStr, setOptionsStr] = useState("");

  const nextPos = useMemo(() => (qs.length ? Math.max(...qs.map(q=>q.position)) + 1 : 0), [qs]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("event_questions")
        .select("*")
        .eq("event_id", eventId)
        .order("position", { ascending: true });
      if (error) setErr(error.message);
      setQs((data ?? []) as Q[]);
      setLoading(false);
    })();
  }, [eventId]);

  function resetAlerts() { setMsg(null); setErr(null); }

  async function addQuestion() {
    resetAlerts();
    if (!label.trim()) { setErr("Label is required."); return; }
    if ((kind === "select" || kind === "multi") && !optionsStr.trim()) {
      setErr("Options are required for select/multi."); return;
    }
    let field_key = slugify(label);
    if (["name","email","phone"].includes(field_key)) field_key = `${field_key}_field`;
    // Make unique per event (avoid unique constraint hit)
    const existing = new Set(qs.map(q => q.field_key));
    let base = field_key, i = 2;
    while (existing.has(field_key)) field_key = `${base}_${i++}`;

    const options = (kind === "select" || kind === "multi")
      ? optionsStr.split(",").map(s => s.trim()).filter(Boolean)
      : [];

    const { error } = await supabase.from("event_questions").insert({
      event_id: eventId, label, field_key, kind, required, options, position: nextPos
    });
    if (error) { setErr(error.message); return; }
    setMsg("Question added.");
    setLabel(""); setKind("text"); setRequired(false); setOptionsStr("");
    // refresh
    const { data } = await supabase
      .from("event_questions").select("*").eq("event_id", eventId).order("position");
    setQs((data ?? []) as Q[]);
  }

  async function saveQuestion(q: Q) {
    resetAlerts();
    const { error } = await supabase.from("event_questions")
      .update({ label: q.label, required: q.required, options: q.options })
      .eq("id", q.id);
    if (error) { setErr(error.message); return; }
    setMsg("Saved.");
  }

  async function removeQuestion(id: string) {
    resetAlerts();
    const { error } = await supabase.from("event_questions").delete().eq("id", id);
    if (error) { setErr(error.message); return; }
    setQs(qs.filter(q => q.id !== id));
    setMsg("Removed.");
  }

  async function move(q: Q, dir: -1 | 1) {
    resetAlerts();
    const curIdx = qs.findIndex(x => x.id === q.id);
    const swapIdx = curIdx + dir;
    if (swapIdx < 0 || swapIdx >= qs.length) return;
    const a = qs[curIdx], b = qs[swapIdx];
    const { error: e1 } = await supabase.from("event_questions").update({ position: b.position }).eq("id", a.id);
    const { error: e2 } = await supabase.from("event_questions").update({ position: a.position }).eq("id", b.id);
    if (e1 || e2) { setErr((e1||e2)?.message || "Reorder failed"); return; }
    const copy = [...qs]; [copy[curIdx], copy[swapIdx]] = [copy[swapIdx], copy[curIdx]];
    setQs(copy);
  }

  function setQ<K extends keyof Q>(id: string, key: K, val: Q[K]) {
    setQs(prev => prev.map(q => q.id === id ? { ...q, [key]: val } as Q : q));
  }
  if (loading) {
    return (
      <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 12, marginBottom: 12 }}>
        Loading questions…
      </div>
    );
  }

  return (
    <div style={card}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8 }}>
        <div style={{ fontWeight:700 }}>Custom Questions</div>
        <div style={{ display:"flex", gap:8 }}>
          {/* Presets */}
          <button style={tinyBtn} onClick={() => { setLabel("Can you volunteer?"); setKind("bool"); setRequired(false); setOptionsStr(""); }}>+ Volunteer?</button>
          <button style={tinyBtn} onClick={() => { setLabel("Would you like a yard sign?"); setKind("bool"); setRequired(false); setOptionsStr(""); }}>+ Yard sign?</button>
          <button style={tinyBtn} onClick={() => { setLabel("Preferred contact method"); setKind("select"); setRequired(false); setOptionsStr("Text, Phone, Email"); }}>+ Contact method</button>
        </div>
      </div>

      {(msg || err) && (
        <div style={{
          marginBottom:10, padding:"10px 12px", borderRadius:10,
          background: msg ? "#ECFDF5" : "#FEF2F2",
          border:`1px solid ${msg ? "#A7F3D0" : "#FCA5A5"}`,
          color: msg ? "#065F46" : "#991B1B"
        }}>{msg || err}</div>
      )}

      {/* Add new */}
      <div style={{ ...card, margin:0, padding:0, border:"none", background:"transparent" }}>
        <div style={row}>
          <input style={input} placeholder="Question label (e.g., Can you volunteer?)" value={label} onChange={e=>setLabel(e.target.value)} />
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8 }}>
            <select style={input} value={kind} onChange={e=>setKind(e.target.value as Q["kind"])}>
              {KINDS.map(k => <option key={k} value={k}>{k}</option>)}
            </select>
            <label style={{ display:"flex", alignItems:"center", gap:8 }}>
              <input type="checkbox" checked={required} onChange={e=>setRequired(e.target.checked)} /> Required
            </label>
            {(kind === "select" || kind === "multi") && (
              <input style={input} placeholder="Options (comma-separated)" value={optionsStr} onChange={e=>setOptionsStr(e.target.value)} />
            )}
          </div>
          <button style={primaryBtn} onClick={addQuestion}>Add Question</button>
        </div>
      </div>

      {/* Existing list */}
      <div style={{ marginTop:12 }}>
        {qs.map((q, idx) => {
          const canUp = idx > 0, canDown = idx < qs.length - 1;
          const optionsLocal = Array.isArray(q.options) ? q.options : [];
          const optsStr = optionsLocal.join(", ");
          return (
            <div key={q.id} style={{ borderTop:"1px solid #f1f5f9", paddingTop:10, marginTop:10 }}>
              <div style={{ display:"grid", gridTemplateColumns:"1fr auto", gap:8 }}>
                <div style={{ fontWeight:700 }}>{q.label}</div>
                <div style={{ display:"flex", gap:6 }}>
                  <button style={tinyBtn} onClick={()=>move(q,-1)} disabled={!canUp}>↑</button>
                  <button style={tinyBtn} onClick={()=>move(q, 1)} disabled={!canDown}>↓</button>
                  <button style={{ ...tinyBtn, borderColor:"#ef4444", color:"#ef4444" }} onClick={()=>removeQuestion(q.id)}>Delete</button>
                </div>
              </div>

              <div style={{ display:"grid", gridTemplateColumns:"1fr 160px 120px", gap:8, marginTop:8 }}>
                <input style={input} value={q.label} onChange={e=>setQ(q.id,"label", e.target.value)} />
                <input style={{ ...input, background:"#f9fafb" }} value={q.kind} disabled />
                <label style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <input type="checkbox" checked={q.required} onChange={e=>setQ(q.id,"required", e.target.checked)} /> Required
                </label>
              </div>

              {(q.kind === "select" || q.kind === "multi") && (
                <div style={{ marginTop:8 }}>
                  <input
                    style={input}
                    value={optsStr}
                    onChange={e=>setQ(q.id,"options", e.target.value.split(",").map(s=>s.trim()).filter(Boolean) as any)}
                    placeholder="Options (comma-separated)"
                  />
                </div>
              )}

              <div style={{ marginTop:8 }}>
                <button style={btn} onClick={()=>saveQuestion(q)}>Save</button>
                <span style={{ color:"#6b7280", marginLeft:8 }}>field_key: <code>{q.field_key}</code></span>
              </div>
            </div>
          );
        })}
        {qs.length === 0 && <div style={{ color:"#6b7280" }}>No custom questions yet.</div>}
      </div>
    </div>
  );
}
